import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useConnection, useDisconnect } from "wagmi";
import { Button, Copy, Row, Sheet, Pill } from "../components/ui";
import { Link } from "../components/Link";
import { pt } from "../sdk";
import { useAccount, useOwner, useStreamStatus } from "../state/account";
import { enableTrading, useKeyState } from "../state/actions";
import { useValuations } from "../state/valuation";
import { pnl, tone, usd } from "../lib/num";
import { until } from "../lib/time";
import { shortAddress } from "../wallet/address";

const STREAM_WORDS = { off: "Off", connecting: "Connecting", live: "Live", reconnecting: "Reconnecting" } as const;

export function SessionKeyCard() {
  const owner = useOwner();
  const keyState = useKeyState();
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);
  const info = useQuery({
    queryKey: ["sessionKey", owner, keyState.data],
    queryFn: () => pt.sessionKey(owner as string),
    enabled: owner !== null,
  });
  if (!owner) return null;
  const state = keyState.data;
  const refresh = () => client.invalidateQueries({ queryKey: ["keyState"] }).then(() => client.invalidateQueries({ queryKey: ["sessionKey"] }));
  return (
    <div className="stack-sm">
      <div className="field-head">
        <span className="label">One-tap bets</span>
        {state === "ready" ? <Pill tone="up">On</Pill> : state === "unregistered" ? <Pill tone="warn">Needs signature</Pill> : <Pill>Off</Pill>}
      </div>
      {info.data && state === "ready" && (
        <>
          <Row label="Session key">{shortAddress(info.data.address)}</Row>
          <Row label="Expires in">{until(info.data.heldUntilMs)}</Row>
        </>
      )}
      <p className="note">
        Bets without wallet prompts. It can never withdraw. 30 days.
      </p>
      <div className="chips">
        {state !== "ready" ? (
          <Button
            tone="primary"
            block
            busy={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await enableTrading();
              } catch {
                // told by a toast
              } finally {
                setBusy(false);
                void refresh();
              }
            }}
          >
            Turn on one-tap bets
          </Button>
        ) : (
          <Button
            tone="ghost"
            size="sm"
            onClick={async () => {
              await pt.dropSession(owner);
              void refresh();
            }}
          >
            Forget this key
          </Button>
        )}
      </div>
    </div>
  );
}

export function AccountSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { address } = useConnection();
  const { mutate: disconnect } = useDisconnect();
  const account = useAccount();
  const valued = useValuations();
  const stream = useStreamStatus();
  if (!address) return null;
  const owner = address.toLowerCase();
  return (
    <Sheet open={open} onClose={onClose} title="Account" side>
      <div className="stack-lg">
        <div className="stack-sm">
          <Copy text={owner}>{owner}</Copy>
          <div className="account-links">
            <a href={`https://hyperevmscan.io/address/${owner}`} target="_blank" rel="noreferrer">
              HyperEVM ↗
            </a>
            <a href={`https://app.hyperliquid.xyz/explorer/address/${owner}`} target="_blank" rel="noreferrer">
              Hyperliquid ↗
            </a>
          </div>
        </div>

        <div className="stack-sm">
          <Row label="Balance">{valued ? usd(valued.equity) : "—"}</Row>
          <Row label="Live bets" tone={valued ? tone(valued.openPnl) : undefined}>
            {valued ? pnl(valued.openPnl) : "—"}
          </Row>
          <Row label="Available">{account ? usd(account.funds.availableBalance) : "—"}</Row>
          <Row label="Queued">{account ? usd(account.queued) : "—"}</Row>
          <Row label="In bets">{account ? usd(account.locked) : "—"}</Row>
          <Row label="Account stream">{STREAM_WORDS[stream]}</Row>
          <div className="chips">
            <Link href="/wallet" className="btn btn-secondary btn-sm btn-block" onClick={onClose}>
              Wallet
            </Link>
          </div>
        </div>

        <SessionKeyCard />

        <Button
          tone="danger"
          block
          onClick={() => {
            disconnect();
            onClose();
          }}
        >
          Disconnect
        </Button>
      </div>
    </Sheet>
  );
}
